import { callAPI } from "../../network/networkConnection";
import { NetworkConfiguration } from "../../network/networkConfriguation";
import { fetchToken } from "../../lib/globalApiClient";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const searchProductList = (formData, method) => {
  return new Promise(async (resolve, reject) => {
    const base = process.env.REACT_APP_APIUrl1688;
    const path = (method || "get-product-info").startsWith("/") ? method : `/${method || "get-product-info"}`;
    const url = base ? `${base}${path}` : `http://localhost:3000${path}`;
    const maxRetries = 3;
    const backoffMs = [1500, 3000, 6000];

    try {
      const token = await fetchToken();
      const headers = {
        "Content-Type": "application/json",
        "X-API-Token": token
      };

      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const res = await callAPI(url, "POST", headers, formData);
          resolve(res);
          return;
        } catch (err) {
          const is429 = err?.status === 429 || (err?.message && String(err.message).includes("429"));
          if (is429 && attempt < maxRetries) {
            const wait = backoffMs[attempt] || 6000;
            await sleep(wait);
            continue;
          }
          reject(err);
          return;
        }
      }
    } catch (tokenError) {
      reject(tokenError);
    }
  });
};

// Fetch Alibaba product detail by offerId (AWS endpoint provided via curl)
const getAlibabaProductDetailByOfferId = (offerId, country = "en") => {
  const baseUrl = process.env.REACT_APP_APIUrl1688;
  const url = baseUrl + "/product-detail-by-offerid";
  const payload = JSON.stringify({ offerId, country });
  return new Promise(async (resolve, reject) => {
    try {
      const token = await fetchToken();
      callAPI(
        url,
        "POST",
        {
          "Content-Type": "application/json",
          "X-API-Token": token
        },
        payload
      )
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    } catch (tokenError) {
      reject(tokenError);
    }
  });
};

const getAllProductsList = (queryParams) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.ADMIN_PRODUCTS +
      queryParams,
      "GET",
      {
        "Content-Type": "application/json",
      }
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const getProductCount = () => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.ADMIN_PRODUCTS +
      "/count",
      "GET",
      {
        "Content-Type": "application/json",
      }
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const getProductById = (id) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.PRODUCT_BY_ID + id,
      "GET",
      {
        "Content-Type": "application/json",
      }
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
const getVariationProductById = (id) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.PRODUCT_VARIATION_BY_PARENT_ID +
      id,
      "GET",
      {
        "Content-Type": "application/json",
      }
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
const addProducts = (formData) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.ADD_PRODUCTS,
      "POST",
      {},
      formData
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const importAlibabaRaw = (products) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.IMPORT_ALIBABA_RAW,
      "POST",
      { "Content-Type": "application/json" },
      JSON.stringify({ products })
    )
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

/** GET global categories from DB (globalCategory collection): main categories + subcategories for Alibaba product search */
const getGlobalCategories = () => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.GLOBAL_CATEGORIES,
      "GET",
      { "Content-Type": "application/json" }
    )
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

const editProducts = (productPayload, id) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.EDIT_PRODUCTS +
      `/${id}`,
      "PUT",
      {
        "Content-Type": "application/json",
      },
      productPayload
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
const updateProductVariant = (id, variantPayload) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.EDIT_PRODUCTS +
      `/${id}/variant`,
      "PUT",
      {
        "Content-Type": "application/json",
      },
      JSON.stringify(variantPayload)
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const getProductPriceLogs = (id, queryString = "") => {
  const qs = queryString ? (queryString.startsWith("?") ? queryString : `?${queryString}`) : "";
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.EDIT_PRODUCTS +
      `/${id}/price-logs${qs}`,
      "GET",
      { "Content-Type": "application/json" }
    )
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

const uploadImage = (productPayload, id) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.UPLOAD_IMAGE +
      `/${id}/images`,
      "PUT",
      {},
      productPayload
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const deleteProducts = (id) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.DELETE_PRODUCT +
      `/${id}`,
      "DELETE",
      {
        "Content-Type": "application/json",
      }
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
const addExcelProduct = (formData) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.UPLOAD_EXCEL_PRODUCT,
      "POST",
      {},
      formData
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
const getProductBySku = (sku) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.PRODUCT_BY_SKU + sku,
      "GET",
      {
        "Content-Type": "application/json",
      }
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

// Get products by multiple SKUs (for cross-border order processing)
const getProductsBySkus = (skus) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + "admin/products/by-skus",
      "POST",
      { "Content-Type": "application/json" },
      JSON.stringify({ skus })
    )
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

const bulkUpdateVerifiedSeller = (productIds) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.ADMIN_PRODUCTS + "/verify-seller",
      "PUT",
      {
        "Content-Type": "application/json",
      },
      JSON.stringify({ productIds })
    )
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const getSellerSummary = () => {
  return callAPI(
    process.env.REACT_APP_APIUrl + NetworkConfiguration.SELLER_SUMMARY,
    "GET",
    { "Content-Type": "application/json" }
  );
};

const getProductsWithCategoryPath = (queryString = "") => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl +
      NetworkConfiguration.ADMIN_PRODUCTS_WITH_CATEGORY_PATH +
      (queryString || ""),
      "GET",
      { "Content-Type": "application/json" }
    )
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

const updateCategoryApproval = (payload) => {
  return new Promise((resolve, reject) => {
    callAPI(
      process.env.REACT_APP_APIUrl + NetworkConfiguration.UPDATE_CATEGORY_APPROVAL,
      "PUT",
      { "Content-Type": "application/json" },
      JSON.stringify(payload)
    )
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  });
};

// Supplier Data Info APIs
const getSuppliersList = (queryParams = "") => {
  return callAPI(
    process.env.REACT_APP_APIUrl + NetworkConfiguration.SUPPLIER_DATA_INFO + queryParams,
    "GET",
    { "Content-Type": "application/json" }
  );
};

const getSupplierStats = () => {
  return callAPI(
    process.env.REACT_APP_APIUrl + NetworkConfiguration.SUPPLIER_DATA_INFO_STATS,
    "GET",
    { "Content-Type": "application/json" }
  );
};

const getSupplierDetails = (sellerOpenId) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + NetworkConfiguration.SUPPLIER_DATA_INFO + "/" + encodeURIComponent(sellerOpenId),
    "GET",
    { "Content-Type": "application/json" }
  );
};

const getSupplierProducts = (sellerOpenId, queryParams = "") => {
  return callAPI(
    process.env.REACT_APP_APIUrl + NetworkConfiguration.SUPPLIER_DATA_INFO + "/" + encodeURIComponent(sellerOpenId) + "/products" + queryParams,
    "GET",
    { "Content-Type": "application/json" }
  );
};

const getSupplierOrderDetails = (sellerOpenId, queryParams = "") => {
  return callAPI(
    process.env.REACT_APP_APIUrl + NetworkConfiguration.SUPPLIER_DATA_INFO + "/" + encodeURIComponent(sellerOpenId) + "/orders" + queryParams,
    "GET",
    { "Content-Type": "application/json" }
  );
};

// Tags Management API functions
const getTagProducts = (tag, page = 1, limit = 25, options = {}) => {
  let url = process.env.REACT_APP_APIUrl + `admin/tags/${encodeURIComponent(tag)}/products?page=${page}&limit=${limit}`;
  if (options.parent === true) {
    url += "&parent=true";
  }
  if (options.includeCommonKeywords === true) {
    url += "&includeCommonKeywords=true";
  }
  return callAPI(url, "GET", { "Content-Type": "application/json" });
};

const bulkAddTagToProducts = (newTags, productIds) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/tags/bulk-add",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ newTags, productIds })
  );
};

const getAllTags = (page = 1, limit = 20, search = '') => {
  let url = process.env.REACT_APP_APIUrl + `admin/tags?page=${page}&limit=${limit}`;
  if (search && search.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }
  return callAPI(url, "GET", { "Content-Type": "application/json" });
};

const updateTag = (oldTag, newTag) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/tags/update",
    "PUT",
    { "Content-Type": "application/json" },
    JSON.stringify({ oldTag, newTag })
  );
};

const toggleHideTag = (tag) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/tags/toggle-hide",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ tag })
  );
};

// Tag Hierarchy Management
const getTagHierarchies = () => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/tags/hierarchies",
    "GET",
    { "Content-Type": "application/json" }
  );
};

const setTagParentChild = (parentTag, childTags) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/tags/hierarchy",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ parentTag, childTags })
  );
};

const deleteTagParent = (parentTag) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/tags/hierarchy",
    "DELETE",
    { "Content-Type": "application/json" },
    JSON.stringify({ parentTag })
  );
};

// ParentTags Management (new collection)
const getParentTags = () => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/parent-tags",
    "GET",
    { "Content-Type": "application/json" }
  );
};

const setParentTags = (parentTag, tags) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/parent-tags",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ parentTag, tags })
  );
};

const deleteParentTag = (parentTag) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/parent-tags",
    "DELETE",
    { "Content-Type": "application/json" },
    JSON.stringify({ parentTag })
  );
};

// Category Keyword Mapping API functions
const getCategoriesFlat = (search = '') => {
  let url = process.env.REACT_APP_APIUrl + 'admin/categories/flat';
  if (search) url += `?search=${encodeURIComponent(search)}`;
  return callAPI(url, "GET", { "Content-Type": "application/json" });
};

/** GET category tree (nested parent > child) for Map Category modal */
const getCategoryTree = () => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/category",
    "GET",
    { "Content-Type": "application/json" }
  );
};

/** Lightweight tree for Map Category modal - faster load, minimal payload */
const getCategoryTreeLight = () => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/categories/tree-light",
    "GET",
    { "Content-Type": "application/json" }
  );
};

const mapKeywordsToCategory = (categoryId, tag, classification, keywords) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/categories/map-keywords",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ categoryId, tag, classification, keywords })
  );
};

const getKeywordMappingsForTag = (tag) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + `admin/tags/${encodeURIComponent(tag)}/keyword-mappings`,
    "GET",
    { "Content-Type": "application/json" }
  );
};

const updateKeywordMapping = (categoryId, tag, classification, payload) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/categories/keyword-mapping",
    "PATCH",
    { "Content-Type": "application/json" },
    JSON.stringify({ categoryId, tag, classification, ...payload })
  );
};

const deleteKeywordMapping = (categoryId, tag, classification) => {
  return callAPI(
    process.env.REACT_APP_APIUrl + "admin/categories/keyword-mapping",
    "DELETE",
    { "Content-Type": "application/json" },
    JSON.stringify({ categoryId, tag, classification })
  );
};

export const productListApiService = {
  getAllProductsList,
  getProductCount,
  getProductById,
  editProducts,
  updateProductVariant,
  getProductPriceLogs,
  deleteProducts,
  uploadImage,
  addProducts,
  addExcelProduct,
  getVariationProductById,
  getProductBySku,
  getProductsBySkus,
  searchProductList,
  getAlibabaProductDetailByOfferId,
  importAlibabaRaw,
  getGlobalCategories,
  bulkUpdateVerifiedSeller,

  getSellerSummary,
  getProductsWithCategoryPath,
  updateCategoryApproval,

  // Supplier Data Info
  getSuppliersList,
  getSupplierStats,
  getSupplierDetails,
  getSupplierProducts,
  getSupplierOrderDetails,

  // Tags Management
  getTagProducts,
  bulkAddTagToProducts,
  getAllTags,
  updateTag,
  toggleHideTag,

  // Tag Hierarchy
  getTagHierarchies,
  setTagParentChild,
  deleteTagParent,

  // ParentTags (new collection)
  getParentTags,
  setParentTags,
  deleteParentTag,

  // Category Keyword Mapping
  getCategoriesFlat,
  getCategoryTree,
  getCategoryTreeLight,
  mapKeywordsToCategory,
  getKeywordMappingsForTag,
  updateKeywordMapping,
  deleteKeywordMapping,
};

// Also export as productApiService for compatibility
export const productApiService = productListApiService;
